import { withLoading } from "@/api/api";
import { normalizarPagina } from "@/helpers/apuracaoCapex";
import { get, post } from "./http";

/**
 * Service da tela `enquadramento.vue` (API real).
 * `GET`/`POST v1/apuracaocapex/{empresaId}/enquadramentos` — a API já devolve horas,
 * benefícios/dispêndios e os contadores por situação.
 *
 * @typedef {object} EnquadramentoLinha
 * @property {number} id - id do projeto (`item-key` e payload de gravação)
 * @property {string} codigo
 * @property {string} nome
 * @property {number} trabalho - 1 = LB · 2 = LI · 3 = os dois
 * @property {Array<{trabalho: number, label: string}>} beneficios
 * @property {string} enquadramento - "Sim" | "Não" (hoje sempre "Sim": só vêm aprovados)
 * @property {number|null} tipo_pesquisa_id
 * @property {string|null} tipo_pesquisa - rótulo (`null` ⇒ exibir "Indefinido")
 * @property {number} horas_timesheet
 * @property {Array<{trabalho: number, label: string, valor: number}>} dispendios
 * @property {string} ativo_intangivel - "Sim" | "Não" | "" (vazio = não avaliado)
 * @property {string|null} observacao
 * @property {number|null} usuario_id
 * @property {string|null} usuario - quem fez a última marcação
 * @property {string|null} marcado_em
 */

const LOADING_KEY = "apuracaocapex-enquadramento";
const LOADING_KEY_SALVAR = "apuracaocapex-enquadramento-salvar";


/** Opções do filtro de benefício → `filters[trabalho]`. */
export const BENEFICIOS = [
  { text: "Todos", value: "" },
  { text: "Lei do Bem", value: "1" },
  { text: "Lei de Informática", value: "2" },
];

/** Opções do filtro de situação → `filters[ativo_intangivel]`. */
export const SITUACOES = [
  { text: "Todas", value: "" },
  { text: "Ativo intangível (Sim)", value: "sim" },
  { text: "Não é ativo intangível (Não)", value: "nao" },
  { text: "Não avaliado", value: "nao_avaliado" },
];

/** Rótulo curto do benefício, usado nas pílulas da tabela (LB / LI). */
export const rotuloCurtoBeneficio = (trabalho) => {
  const valor = Number(trabalho);

  if (valor === 1) return "LB";
  if (valor === 2) return "LI";

  return "???";
};

/** Nome completo do benefício (tooltips das pílulas). */
export const labelBeneficio = (trabalho) => {
  const valor = Number(trabalho);

  if (valor === 1) return "Lei do Bem";
  if (valor === 2) return "Lei de Informática";
  if (valor === 3) return "Lei do Bem e Lei de Informática";

  return "-";
};

/** Rótulo do tipo de pesquisa — a API manda o rótulo pronto; `null` ⇒ "Indefinido". */
export const labelTipoPesquisa = (valor) => valor || "Indefinido";

/** Texto digitado no filtro, sem espaços nas pontas. */
const texto = (valor) => String(valor || "").trim();

/** Filtros no formato da API (só o que o usuário escolheu; lista vazia = sem filtro). */
const montarFiltros = ({ nome, trabalho, ativoIntangivel } = {}) => {
  const busca = texto(nome);

  return {
    nome: busca ? [busca] : [],
    trabalho: trabalho ? [trabalho] : [],
    ativo_intangivel: ativoIntangivel ? [ativoIntangivel] : [],
  };
};

/**
 * Lista paginada, filtrada e ordenada.
 *
 * ⚠️ A API **não aceita** `per_page = -1`: com `perPage <= 0` (opção "Todos")
 * pedimos 1 registro só para descobrir o `meta.total` e repetimos a busca com esse tamanho.
 *
 * @param {{ empresaId: number|string, anoBase: string|number, page?: number, perPage?: number,
 *   sort?: object|Array, filtros?: { nome?: string, trabalho?: string, ativoIntangivel?: string } }} params
 * @returns {Promise<{ rows: EnquadramentoLinha[], total: number, page: number, perPage: number,
 *   totais: { sim: number, nao: number, nao_avaliado: number } }>}
 */
export const listarEnquadramentos = async ({
  empresaId,
  anoBase,
  page = 1,
  perPage = 10,
  sort = {},
  filtros = {},
}) => {
  const buscar = async () => {
    const params = {
      ano_base: Number(anoBase) || undefined,
      page,
      per_page: perPage > 0 ? perPage : 1,
      filters: montarFiltros(filtros),
      sort,
    };

    let corpo = await get(empresaId, "enquadramentos", params);

    if (perPage <= 0) {
      const total = (corpo.meta && corpo.meta.total) || 0;

      if (total > 1) {
        corpo = await get(empresaId, "enquadramentos", { ...params, page: 1, per_page: total });
      }
    }

    return normalizarPagina(corpo, { perPage: perPage > 0 ? perPage : 10 });
  };

  return withLoading(LOADING_KEY, buscar);
};

/**
 * Grava **marcação** e/ou **observação** — sempre em lote (`projetos` é lista, mesmo com um
 * projeto só). Só as chaves enviadas são alteradas (a marcação em lote **não** apaga
 * observações) e o usuário vem do token — **não** enviamos `usuario`.
 *
 * @param {{ empresaId: number|string, anoBase: string|number, projetos: Array<number>,
 *   ativoIntangivel?: boolean|null, observacao?: string|null }} params
 *   `ativoIntangivel: null` volta para "Não avaliado"; `observacao: null` apaga a observação.
 * @returns {Promise<Array>} registros gravados
 */
export const salvarEnquadramento = async ({ empresaId, anoBase, projetos, ativoIntangivel, observacao }) =>
  withLoading(LOADING_KEY_SALVAR, async () => {
    const corpo = { ano_base: Number(anoBase) || undefined, projetos };

    if (ativoIntangivel !== undefined) corpo.ativo_intangivel = ativoIntangivel;
    if (observacao !== undefined) corpo.observacao = observacao;

    const resposta = await post(empresaId, "enquadramentos", corpo);

    return (resposta && resposta.data) || [];
  });

export default {
  BENEFICIOS,
  SITUACOES,
  rotuloCurtoBeneficio,
  labelBeneficio,
  labelTipoPesquisa,
  listarEnquadramentos,
  salvarEnquadramento,
};
